import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';

type Claim = {
  id: string;
  state: string;
  reference: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
  period: { periodNo: number; start: string; end: string; dataDate: string; status: string };
  submittedBy: { name: string } | null;
  _count: { lines: number };
};

type Period = {
  id: string;
  periodNo: number;
  start: string;
  end: string;
  dataDate: string;
  status: string;
};

export const CLAIM_CHIP: Record<string, string> = {
  DRAFT: '',
  SUBMITTED: 'blue',
  UNDER_VERIFICATION: 'warn',
  RETURNED: 'crit',
  VERIFIED: 'good',
  DISPUTED: 'crit',
  CERTIFIED: 'good',
};

export const CLAIM_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Diajukan',
  UNDER_VERIFICATION: 'Sedang diverifikasi',
  RETURNED: 'Dikembalikan',
  VERIFIED: 'Terverifikasi',
  DISPUTED: 'Dibantah',
  CERTIFIED: 'Tersertifikasi',
};

export function Claims({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [claims, setClaims] = useState<Claim[] | null>(null);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.get<{ claims: Claim[] }>(`/progress/claims?projectId=${projectId}`),
        api.get<{ periods: Period[] }>(`/programme/periods?projectId=${projectId}`),
      ]);
      setClaims(c.claims);
      setPeriods(p.periods);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load claims.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openFor(periodId: string) {
    setBusy(true);
    setNotice(null);
    try {
      const r = await api.post<{ claimId: string; lines: number; blocked: number; programme: string }>(
        '/progress/claims',
        { projectId, periodId },
      );
      setNotice(
        `Klaim dibuka dari ${r.programme}: ${r.lines} baris` +
          (r.blocked > 0 ? `, ${r.blocked} terhalang deliverable yang belum IFC.` : '.'),
      );
      await load();
      nav(`/claims/${r.claimId}`);
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal membuka klaim.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!claims) return <Loading rows={4} />;

  const claimed = new Set(claims.map((c) => c.period.periodNo));
  const open = periods.filter((p) => !claimed.has(p.periodNo) && p.status !== 'CLOSED');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Klaim progres</h1>
          <p>Angka kontraktor dan angka lapangan, disimpan berdampingan. Selisihnya yang dilaporkan.</p>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      {open.length > 0 && (
        <section className="panel" style={{ marginBottom: 20 }}>
          <header className="panel-head">
            <h3>Periode tanpa klaim</h3>
            <p>Baris klaim disemai dari versi programme yang diterima terakhir.</p>
          </header>
          <div className="filters">
            {open.map((p) => (
              <button key={p.id} className="btn btn-ghost" disabled={busy} onClick={() => openFor(p.id)}>
                Buka klaim P{p.periodNo}
                <span className="hint" style={{ marginLeft: 8 }}>
                  {fmtDate(p.start)} – {fmtDate(p.end)}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {claims.length === 0 ? (
        <Empty
          title="Belum ada klaim"
          body={
            periods.length === 0
              ? 'Belum ada periode pelaporan. Buat satu lebih dulu, lalu klaim bisa dibuka di sana.'
              : 'Buka klaim untuk salah satu periode di atas.'
          }
        />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Periode</th>
                <th>Rentang</th>
                <th>Baris</th>
                <th>Status</th>
                <th>Diajukan</th>
                <th>Diverifikasi</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <tr key={c.id} onClick={() => nav(`/claims/${c.id}`)}>
                  <td className="num">P{c.period.periodNo}</td>
                  <td>
                    {fmtDate(c.period.start)} – {fmtDate(c.period.end)}
                  </td>
                  <td>{c._count.lines}</td>
                  <td>
                    <span className={`chip ${CLAIM_CHIP[c.state] ?? ''}`}>
                      {CLAIM_LABEL[c.state] ?? c.state}
                    </span>
                  </td>
                  <td>{c.submittedAt ? fmtDate(c.submittedAt) : '—'}</td>
                  <td>{c.verifiedAt ? fmtDate(c.verifiedAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
